import type { ChangeEvent, MouseEvent } from "react";
import type { BusinessProfile, BusinessProfileInput } from "./business-profile";

export type CustomerSection = "plan" | "setup";

export const pathFor = (section: CustomerSection) =>
  section === "setup" ? "/setup" : "/";

export const sectionForPath = (pathname: string): CustomerSection =>
  pathname.startsWith("/setup") ? "setup" : "plan";

export const parseList = (value: string) =>
  value
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

export const optionalNumber = (value: string) => {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

export function createBusinessProfileChangeHandler(
  draft: BusinessProfile,
  onChange: (profile: BusinessProfile) => void,
) {
  return <K extends keyof BusinessProfileInput>(
    key: K,
    value: BusinessProfileInput[K],
  ) => onChange({ ...draft, [key]: value });
}

export function createServicesChangeHandler(
  change: ReturnType<typeof createBusinessProfileChangeHandler>,
) {
  return (event: ChangeEvent<HTMLTextAreaElement>) => {
    change("services", parseList(event.target.value));
  };
}

export function createLeadSourcesChangeHandler(
  change: ReturnType<typeof createBusinessProfileChangeHandler>,
) {
  return (event: ChangeEvent<HTMLTextAreaElement>) => {
    change("leadSources", parseList(event.target.value));
  };
}

export function createOptionalNumberChangeHandler(
  change: ReturnType<typeof createBusinessProfileChangeHandler>,
  key:
    | "monthlyRevenueTarget"
    | "weeklyCapacityHours"
    | "minimumEngagementValue",
) {
  return (event: ChangeEvent<HTMLInputElement>) => {
    change(key, optionalNumber(event.target.value));
  };
}

export function createPreferredContactMethodChangeHandler(
  change: ReturnType<typeof createBusinessProfileChangeHandler>,
) {
  return (event: ChangeEvent<HTMLSelectElement>) => {
    change(
      "preferredContactMethod",
      event.target.value as BusinessProfileInput["preferredContactMethod"],
    );
  };
}

export function createEmailInputChangeHandler(
  setEmail: (value: string) => void,
) {
  return (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };
}

export function createTextFieldChangeHandler(
  change: ReturnType<typeof createBusinessProfileChangeHandler>,
  key: "businessName" | "location" | "marketFocus",
) {
  return (event: ChangeEvent<HTMLInputElement>) => {
    change(key, event.target.value);
  };
}

export function createConfigureClickHandler(
  onConfigure: (fieldId?: string) => void,
  fieldId?: string,
) {
  return () => onConfigure(fieldId);
}

export function createCustomerNavClickHandler(
  onNavigate: (section: CustomerSection) => void,
  section: CustomerSection,
) {
  return (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onNavigate(section);
  };
}

export function createReloadPageHandler() {
  return () => location.reload();
}

export function createSectionPopStateHandler(
  setSection: (section: CustomerSection) => void,
) {
  return () => setSection(sectionForPath(location.pathname));
}

export function createCustomerNavigateHandler(options: {
  setSaved: (value: string) => void;
  navigate: (next: CustomerSection, fieldId?: string) => void;
}) {
  return (next: CustomerSection) => {
    options.setSaved("");
    options.navigate(next);
  };
}

export function createSetupNavigateHandler(options: {
  setSaved: (value: string) => void;
  navigate: (next: CustomerSection, fieldId?: string) => void;
}) {
  return (fieldId?: string) => {
    options.setSaved("");
    options.navigate("setup", fieldId);
  };
}

export function createResetEmailSignInHandler(options: {
  setEmail: (value: string) => void;
  setEmailMessage: (value: string) => void;
  setEmailState: (value: "idle" | "sending" | "sent") => void;
}) {
  return () => {
    options.setEmail("");
    options.setEmailMessage("");
    options.setEmailState("idle");
  };
}

export function createCustomerNavigateFn(
  setSection: (section: CustomerSection) => void,
) {
  return (next: CustomerSection, fieldId?: string) => {
    const path = pathFor(next);
    if (location.pathname !== path) history.pushState({}, "", path);
    setSection(next);
    if (fieldId)
      requestAnimationFrame(() => document.getElementById(fieldId)?.focus());
  };
}

export function createInitialCustomerSection() {
  return sectionForPath(location.pathname);
}
